import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { MemberSelect } from './MemberSelect';
import { useCreateContribution } from '../../hooks/useQueries';
import type { CreateContributionRequest } from '../../services';
import { Thai } from '../../utils/thai';

const quickContributionSchema = z.object({
  memberId: z.string().min(1, 'กรุณาเลือกสมาชิก'),
  amount: z.number().min(1, 'จำนวนเงินต้องมากกว่า 0'),
  year: z.number().min(2023, 'ปีไม่ถูกต้อง'),
  month: z.number().min(1).max(12, 'เดือนไม่ถูกต้อง'),
  paymentDate: z.string().min(1, 'วันที่ชำระเป็นข้อมูลที่จำเป็น'),
  paymentMethod: z.enum(['cash', 'transfer']),
  notes: z.string().optional(),
});

type QuickContributionFormData = z.infer<typeof quickContributionSchema>;

export function QuickAddContribution() {
  const form = useForm<QuickContributionFormData>({
    resolver: zodResolver(quickContributionSchema),
    defaultValues: {
      memberId: '',
      amount: 500, // Default monthly contribution
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash', // Default to cash
      notes: '',
    },
  });

  // Create contribution mutation
  const createContributionMutation = useCreateContribution();

  // Handle success
  useEffect(() => {
    if (createContributionMutation.isSuccess) {
      toast.success('บันทึกเงินสมทบสำเร็จ');
      form.reset({
        memberId: '',
        amount: 500,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        notes: '',
      });
    }
  }, [createContributionMutation.isSuccess, form]);

  // Handle error
  useEffect(() => {
    if (createContributionMutation.isError) {
      toast.error('เกิดข้อผิดพลาดในการบันทึกเงินสมทบ');
    }
  }, [createContributionMutation.isError]);

  const handleSubmit = (data: QuickContributionFormData) => {
    const submissionData: CreateContributionRequest = {
      ...data,
      paymentDate: new Date(data.paymentDate).toISOString(),
    };
    createContributionMutation.mutate(submissionData);
  };

  // Generate month options
  const monthOptions = Thai.Date.getMonthNames().map((name, index) => ({
    value: index + 1,
    label: name,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          เพิ่มเงินสมทบด่วน
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="memberId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>สมาชิก</FormLabel>
                  <FormControl>
                    <MemberSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="เลือกหรือค้นหาสมาชิก..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>จำนวนเงิน (บาท)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="50"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>วิธีการชำระ</FormLabel>
                    <FormControl>
                      <select 
                        {...field} 
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="cash">เงินสด</option>
                        <option value="transfer">โอนเงิน</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="month"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>เดือน</FormLabel>
                    <FormControl>
                      <select 
                        {...field} 
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        {monthOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ปี</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="2023"
                        max="2030"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || new Date().getFullYear())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="paymentDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>วันที่ชำระ</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="หมายเหตุเพิ่มเติม (ไม่จำเป็น)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full"
              disabled={createContributionMutation.isPending}
            >
              {createContributionMutation.isPending ? 'กำลังบันทึก...' : 'บันทึกเงินสมทบ'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}